import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { NarrativeForm } from '../../components/Forms/NarrativeForm';
import type { NarrativeFormData } from '../../components/Forms/types';
import { API } from '../../API';
import { ampli } from '../../ampli';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { useCurrentQuota } from '../../contexts/CurrentQuota';
import { toast } from 'react-toastify';
import { parseFormParamsToInitialData, hasFormParams } from '../../utils/parseFormParamsToInitialData';
import { readCaseDraft, markCaseDraftUsed } from '../../utils/caseDraftStorage';
import { createTimeoutSignal } from '../../utils/common';
import { toApiAffiliationTypes } from '../../utils/affiliationTypes';
import { showRequestError } from '../../utils/requestError';
import { Loader } from '../../components/Loader/Loader';
import { getCollectCommentsParameters } from '../../utils/collectComments';

export default function NarrativeAddPageNew() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentUser] = useCurrentUser();
  const [currentQuota] = useCurrentQuota();
  const workspaceId = parseInt(localStorage.getItem('workspace_id') || '0');
  const workspaceConfig = currentUser?.workspace?.config;
  const featureFlags = workspaceConfig?.featureflags;
  const showStoriesParameters = featureFlags?.includes('show_stories_parameters');

  const [initialData, setInitialData] = useState<Partial<NarrativeFormData> | undefined>();
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);

  // Parse URL params to prefill form when coming from Search page.
  // Long payloads arrive as `form_ref` (a localStorage key) instead of inline
  // `form_*` params — see utils/caseDraftStorage.ts.
  useEffect(() => {
    let isActive = true;

    const draftKey = searchParams.get('form_ref');
    let effectiveParams: URLSearchParams | null = null;

    if (draftKey) {
      const draft = readCaseDraft(draftKey);
      if (draft.status === 'found') {
        effectiveParams = new URLSearchParams(draft.params);
      } else if (draft.status === 'used') {
        // A case was already created from this draft (e.g. Back button or a
        // reused tab) — show a clean blank form and drop the stale form_ref.
        setSearchParams({}, { replace: true });
      } else {
        toast.warning(
          t('The search parameters for this case have expired and could not be restored. Please fill in the form manually.'),
        );
      }
    } else if (hasFormParams(searchParams)) {
      effectiveParams = searchParams;
    }

    if (effectiveParams) {
      setIsLoadingInitialData(true);
      parseFormParamsToInitialData(effectiveParams, t, i18n.language)
        .then(data => {
          if (isActive) {
            setInitialData(data);
          }
        })
        .catch(error => showRequestError(error))
        .finally(() => {
          if (isActive) {
            setIsLoadingInitialData(false);
          }
        });
    } else {
      // Also clear the loading flag: a previous in-flight parse won't clear it
      // itself after cleanup flips isActive, which would leave the Loader stuck
      setIsLoadingInitialData(false);
      setInitialData(undefined);
    }

    return () => {
      isActive = false;
    };
  }, [searchParams, setSearchParams, t, i18n.language]);

  const handleSubmit = async (data: NarrativeFormData) => {
    try {
      const narrativeTypes = [
        { type: 'NARRATIVE' },
        { type: 'MANUAL' }, 
        { type: 'TOPIC_MODELING' }
      ];
      
      const currentType = narrativeTypes[data.currentNarrativeType]?.type;
      
      let parameters = null;
      if (['NARRATIVE', 'TOPIC_MODELING'].includes(currentType)) {
  
        let excludingFilters: string[] = [];
        if (data.languagesExclude) excludingFilters.push('languages');
        if (data.countriesExclude) excludingFilters.push('source_origin_country_ids');
        if (data.sourceGroupsExclude) excludingFilters.push('source_group_ids');
        if (data.sourcesExclude) excludingFilters.push('source_ids');
        if (data.affiliationCountriesExclude) {
          excludingFilters.push(data.affiliationAll ? 'source_state_affiliated_all_countries' : 'source_state_affiliated_country_ids');
        }
        if (data.discreditationsExclude && Array.isArray(data.discreditations) && data.discreditations.length > 0) excludingFilters.push('source_discreditated_entity_types');
        if (data.affiliationTypesExclude && Array.isArray(data.affiliationTypes) && data.affiliationTypes.length > 0) excludingFilters.push('source_affiliation_types');
        if (data.feedsExclude) excludingFilters.push('feed_ids');
        if (data.geoGroupExclude) excludingFilters.push('country_group_ids');
        if (data.showBotsOnly === false) excludingFilters.push('source_bots');
        if (data.tagsExclude && Array.isArray(data.tags) && data.tags.length > 0) excludingFilters.push('tags');

        let widgetsConfig = null;
        if (data.widgetsSettings) {
          widgetsConfig = {
            summary_panel: data.summaryPanel?.value || null,
            event_card: data.eventCard?.value || null,
            opinion_card: data.opinionCard?.value || null,
            sentiment_bar: data.sentimentBar?.length > 0 ? data.sentimentBar.map((v) => v.value) : null,
            geo_widget: data.geoWidget,
            top_actors: data.topActors?.value || null,
            actor_types: data.actorTypes,
            evolution_tabs: data.evolutionTabs?.length > 0 ? data.evolutionTabs.map((v) => v.value) : null,
            evolution_breakdowns: data.evolutionBreakdowns?.length > 0 ? data.evolutionBreakdowns.map((v) => v.value) : null,
            evolution_chart: data.evolutionChart,
          };
        }

        const sortingQuery = data.defaultSorting.fieldName
          ? `${data.defaultSorting.isAscending ? '' : '-'}${data.defaultSorting.fieldName}`
          : null;

        parameters = {
          keywords: [data.keywords],
          examples: [],
          start_date: data.startDate ? format(data.startDate, 'yyyy-LL-dd') : null,
          end_date: data.endDate && !data.isCaseOpen ? format(data.endDate, 'yyyy-LL-dd') : null,
          apply_supervised_classifier: data.applySupervisedClassifier,
          source_types: data.sourceTypes,
          source_group_ids: data.sourceGroups.map((v) => v.value),
          languages: data.languages.map((v) => v.value) || [],
          source_ids: data.sources.map((v) => v.value) || [],
          source_origin_country_ids: data.countries.map((v) => v.value) || [],
          sentiment: data.sentiment.map(score => score.value),
          translate_keywords_query: data.isMultilang,
          keywords_query_origin_language: data.isMultilang && data.nativeLang ? data.nativeLang.value : '',
          content_types: data.contentTypes || [],
          create_stories: data.createStories,
          stories_candidates_min_threshold: data.createStories || currentType === 'TOPIC_MODELING' ? data.storiesCandidatesMinThreshold : null,
          stories_grouping_max_distance: data.createStories || currentType === 'TOPIC_MODELING' ? data.storiesGroupingMaxDistance : null,
          enable_stories_summarization: data.createStories ? showStoriesParameters ? data.enableStoriesSummarization : true : false,
          stories_summarization_language: data.createStories && data.enableStoriesSummarization && data.storiesTranslationLanguage ? data.storiesTranslationLanguage.value : null,
          source_state_affiliated_all_countries: data.affiliationAll,
          source_state_affiliated_country_ids: data.affiliationAll ? [] : data.affiliationCountries.map(v => v.value),
          source_affiliation_types: Array.isArray(data.affiliationTypes) ? toApiAffiliationTypes(data.affiliationTypes.map((v) => v.value)) : [],
          feed_ids: data.feeds.map(v => v.value),
          source_discreditated_entity_types: data.discreditations.map((v) => v.value),
          source_bots: data.showBotsOnly !== null ? true : null,
          aspect_based_sentiment: data.isAspectBasedSentimentEnabled,
          aspect_based_sentiment_object: data.isAspectBasedSentimentEnabled ? data.aspectBasedSentimentObject?.value : null,
          aspect_based_sentiment_subject: data.isAspectBasedSentimentEnabled ? data.aspectBasedSentimentSubject : null,
          ...getCollectCommentsParameters(data),
          topic_modeling_entity: currentType === 'TOPIC_MODELING' && data.topicModelingEntity ? data.topicModelingEntity?.value : null,
          topic_modeling_dashboard_dates_preset: data.addDateRangeSettings && data.dateRangePreset && data.topicModelingEntity?.value !== 'OPINIONS_ONLY' ? data.dateRangePreset?.value : null,
          topic_modeling_dashboard_start_date: data.addDateRangeSettings && data.startDatePreset && data.dateRangePreset?.value === 'FIXED' && data.topicModelingEntity?.value !== 'OPINIONS_ONLY' ? format(data.startDatePreset, 'yyyy-LL-dd') : null,
          topic_modeling_dashboard_end_date: data.addDateRangeSettings && data.endDatePreset && data.dateRangePreset?.value === 'FIXED' && data.topicModelingEntity?.value !== 'OPINIONS_ONLY' ? format(data.endDatePreset, 'yyyy-LL-dd') : null,
          topic_modeling_dashboard_sort: data.addDefaultSorting ? sortingQuery : null,
          topic_modeling_translate_language: currentType === 'TOPIC_MODELING' && data.topicModelingTranslationLanguage ? data.topicModelingTranslationLanguage.value : null,
          country_group_ids: data.geoGroup?.length > 0 ? data.geoGroup : [],
          tags: data.tags?.length > 0 ? data.tags : [],
          exclusive_filters: excludingFilters?.length > 0 ? excludingFilters : null,
          topic_modeling_widget_config: widgetsConfig,
          stories_vectors_model: data.storiesVectorsModel ? data.storiesVectorsModel.value : null,
          include_comments_to_stories: data.includeCommentsToStories,
          include_chat_messages_to_stories: data.includeChatMessagesToStories,
        };
      }

      const response = await API.fetch('POST', `/API/v1/narratives`, null, {
        name: data.name,
        description: data.description,
        is_active: data.isActive,
        type: currentType === 'MANUAL' ? 'NARRATIVE' : currentType,
        is_manual: currentType === 'MANUAL',
        parameters: parameters,
        adjustment_suggestions: null,
        confirm_quota_usage: data.confirm_quota_usage,
        is_manually_managed: data.isManuallyManaged,
        project_id: (data.project?.value as number) ?? null,
      }, createTimeoutSignal(120_000));

      // Case created — mark the prefill draft (if any) as used so revisiting
      // the stale URL renders a blank form instead of re-prefilling
      if (response?.id) {
        const draftKey = searchParams.get('form_ref');
        if (draftKey) {
          markCaseDraftUsed(draftKey);
        }
      }

      if (!data.confirm_quota_usage) {
        if (response?.id) {
          ampli.track({
            event_type: 'Case creation',
            event_properties: {
              is_stories_applied: data.createStories,
              is_aspect_based_sentiment_applied: data.isAspectBasedSentimentEnabled,
              workspace_id: workspaceId,
              narrative_id: response.id,
              ...(initialData && { created_from_search: true }),
            }
          });
          // replace: true — the prefilled /narratives/new entry is replaced in
          // history, so Back from the created case returns to the search page
          // instead of a stale creation form
          toast.success(t('Case created') as string);
          navigate(`/narratives/${response.id}`, { replace: true });
        } else {
          return response;
        }
      } else {
        if (response?.id) {
          ampli.track({
            event_type: 'Case creation',
            event_properties: {
              is_stories_applied: data.createStories,
              is_aspect_based_sentiment_applied: data.isAspectBasedSentimentEnabled,
              workspace_id: workspaceId,
              narrative_id: response.id,
              ...(initialData && { created_from_search: true }),
            }
          });
          // replace: true — the prefilled /narratives/new entry is replaced in
          // history, so Back from the created case returns to the search page
          // instead of a stale creation form
          toast.success(t('Case created') as string);
          navigate(`/narratives/${response.id}`, { replace: true });
        }
      }
    } catch (error: unknown) {
      showRequestError(error, t('Create case'));
    }
  };

  // Show loading state while parsing URL params
  if (isLoadingInitialData) {
    return <Loader />;
  }

  return (
    <NarrativeForm
      mode="add"
      initialData={initialData}
      onSubmit={handleSubmit}
    />
  );
}